import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { FIX_FIELDS, FIX_GROUPS, keysToColumns } from "@/lib/intakeFixFields";

const APP_URL = (import.meta.env.VITE_PUBLIC_APP_URL || "https://dashboard.creativeinvestments.africa").replace(/\/$/, "");

export interface FixTargetSubmission {
  id: string;
  full_name: string;
  preferred_name?: string | null;
  email?: string | null;
}

export interface CreatedFixLink {
  submission: FixTargetSubmission;
  url: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  leadId: string;
  submissions: FixTargetSubmission[];
  /** Field keys ticked when the dialog opens (e.g. passport_photo for a flagged upload). */
  presetKeys?: string[];
  onCreated?: (links: CreatedFixLink[]) => void;
}

export function RequestFixDialog({ open, onOpenChange, leadId, submissions, presetKeys = [], onCreated }: Props) {
  const qc = useQueryClient();
  const [keys, setKeys] = useState<string[]>(presetKeys);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKeys(presetKeys);
    setNote("");
  }, [open, presetKeys.join(",")]);

  const grouped = useMemo(
    () => FIX_GROUPS.map((g) => ({ group: g, fields: FIX_FIELDS.filter((f) => f.group === g) })),
    [],
  );

  function toggle(key: string) {
    setKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  async function create() {
    if (!keys.length) { toast.error("Tick at least one item the student must redo"); return; }
    if (!submissions.length) { toast.error("No submissions selected"); return; }
    setSaving(true);
    try {
      const columns = keysToColumns(keys);
      const ids = submissions.map((s) => s.id);

      // Supersede any earlier open request for these submissions
      await (supabase as any)
        .from("intake_fix_requests")
        .update({ status: "cancelled" })
        .in("submission_id", ids)
        .eq("status", "open");

      const { data: auth } = await supabase.auth.getUser();
      const rows = submissions.map((s) => ({
        submission_id: s.id,
        lead_id: leadId,
        requested_fields: columns,
        note: note.trim() || null,
        created_by: auth?.user?.id ?? null,
      }));

      const { data, error } = await (supabase as any)
        .from("intake_fix_requests")
        .insert(rows)
        .select("id, submission_id, token");
      if (error) throw error;

      const links: CreatedFixLink[] = (data as any[]).map((r) => ({
        submission: submissions.find((s) => s.id === r.submission_id)!,
        url: `${APP_URL}/intake-fix/${r.token}`,
      }));

      qc.invalidateQueries({ queryKey: ["intake-fix-requests", leadId] });
      toast.success(`Correction link created for ${links.length} student${links.length === 1 ? "" : "s"}`);
      onOpenChange(false);
      onCreated?.(links);
    } catch (e: any) {
      toast.error(e?.message || "Could not create the correction request");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PencilLine className="h-4 w-4" /> Request a correction</DialogTitle>
          <DialogDescription>
            Tick only what must be redone. Each student gets their own secure link showing just those items — their existing
            submission is updated, they never refill the whole form. Links work once and expire after 7 days.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Students ({submissions.length})</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {submissions.map((s) => <Badge key={s.id} variant="secondary" className="font-normal">{s.full_name}</Badge>)}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">What needs redoing</Label>
              <span className="text-xs text-muted-foreground">
                {keys.length} item{keys.length === 1 ? "" : "s"} selected
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              The student's page shows only the items ticked below — everything else stays exactly as they submitted it.
            </p>
            {grouped.map(({ group, fields }) => {
              const groupKeys = fields.map((f) => f.key);
              const allOn = groupKeys.every((k) => keys.includes(k));
              return (
                <div key={group} className="border rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium">{group}</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() =>
                        setKeys((prev) =>
                          allOn
                            ? prev.filter((k) => !groupKeys.includes(k))
                            : Array.from(new Set([...prev, ...groupKeys])),
                        )
                      }
                    >
                      {allOn ? "Clear" : "Select all"}
                    </Button>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {fields.map((f) => (
                      <label key={f.key} className="flex items-start gap-2 cursor-pointer text-sm">
                        <Checkbox checked={keys.includes(f.key)} onCheckedChange={() => toggle(f.key)} className="mt-0.5" />
                        <span>
                          {f.label}
                          {f.hint && <span className="block text-xs text-muted-foreground">{f.hint}</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>


          <div className="space-y-2">
            <Label htmlFor="fix-note">Note for the student (optional)</Label>
            <Textarea
              id="fix-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. The document you sent is an ID card — we need the photo page of your passport."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={create} disabled={saving || !keys.length}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Create link{submissions.length > 1 ? "s" : ""} &amp; email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
